import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { getCurrentUser } from '../../../store/redux/reducers/currentUserSlice'
import { getCurrentHideSchedule } from '../../../store/redux/reducers/settingsSlice'
import { SIDEBAR_LINK_DEFINITIONS } from '../sidebarLinkConfig'
import { MainMenuLink } from '../../../store/types/components/sidebar/sidebarTypes'
import { SidebarSection } from '../sidebarUtils'
import { RoleName } from '../../../store/types/enums/RoleType'
import { hasRole } from '../../../store/utils'

export const useSidebarLinks = () => {
  const { t } = useTranslation()
  const currentUser = useSelector(getCurrentUser)
  const currentUserId = currentUser?.id
  const currentUserRoles = currentUser?.roles
  const hideSchedule = useSelector(getCurrentHideSchedule)

  const canAccess = useCallback(
    (allowedRoles?: RoleName[]) => {
      if (!allowedRoles || allowedRoles.length === 0) return true
      return hasRole(allowedRoles, currentUserRoles)
    },
    [currentUserRoles]
  )

  const linksBySection = useMemo(() => {
    const context = { currentUserId, hideSchedule }
    return SIDEBAR_LINK_DEFINITIONS.reduce((acc, def) => {
      if (def.allowedRoles && !canAccess(def.allowedRoles)) return acc
      if (def.isHidden?.(context)) return acc
      const link: MainMenuLink = {
        icon: def.icon,
        text: t(def.textKey),
        index: def.pageIndex,
        route: typeof def.getRoute === 'string' ? def.getRoute : def.getRoute(context),
      }
      acc[def.section] = [...(acc[def.section] ?? []), link]
      return acc
    }, {} as Partial<Record<SidebarSection, MainMenuLink[]>>)
  }, [canAccess, currentUserId, hideSchedule, t])

  return { linksBySection }
}
